import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants.js';
import { logStage, STAGES } from '../utils/IPLogger.js';

export default class AnimatedCreditsScene extends Phaser.Scene {
  constructor() {
    super({ key: 'AnimatedCreditsScene' });

    // Scene state
    this.soldier = null;
    this.zombies = [];
    this.bullets = [];
    this.creditsContainer = null;
    this.music = null;
    this.stars = [];

    // Timing control
    this.zombieSpawnTimer = null;
    this.soldierState = 'entering'; // 'entering', 'defending', 'shooting'
    this.shootCooldown = 0;
    this.groundY = 0;
  }

  create() {
    // Log arrival at credits
    logStage(STAGES.CREDITS_START);

    // Black background with slight purple tint (80s aesthetic)
    this.cameras.main.setBackgroundColor('#0a0a1a');

    // Ground position
    this.groundY = GAME_HEIGHT - 100;

    // Create background elements
    this.createBackground();
    this.createGround();
    this.createStarfield();

    // Create animations
    this.createAnimations();

    // Create soldier (starts off-screen left)
    this.createSoldier();

    // Create CRT scanlines (top layer)
    this.createScanlines();

    // Create scrolling credits overlay
    this.createCreditsOverlay();

    // Start victory music
    this.music = this.sound.add('victory_music', {
      loop: true,
      volume: 0.5,
    });
    this.music.play();

    // Setup input handlers
    this.setupInput();

    // Fade in
    this.cameras.main.fadeIn(1000, 0, 0, 0);
  }

  createBackground() {
    // Draw city skyline silhouette
    const graphics = this.add.graphics();
    graphics.setDepth(1);

    // City buildings silhouette
    graphics.fillStyle(0x1a1a2e, 1);
    const buildingHeights = [120, 180, 150, 200, 140, 190, 160, 170, 130, 185, 155, 175];
    let x = 0;
    const buildingWidth = GAME_WIDTH / buildingHeights.length;

    buildingHeights.forEach(height => {
      graphics.fillRect(x, this.groundY - height, buildingWidth - 2, height);
      // Add some window lights
      const windowColor = Phaser.Utils.Array.GetRandom([0x333355, 0x444466, 0x222244]);
      graphics.fillStyle(windowColor, 0.5);
      for (let wy = this.groundY - height + 20; wy < this.groundY - 20; wy += 30) {
        for (let wx = x + 10; wx < x + buildingWidth - 20; wx += 20) {
          if (Math.random() > 0.3) {
            graphics.fillRect(wx, wy, 8, 12);
          }
        }
      }
      graphics.fillStyle(0x1a1a2e, 1);
      x += buildingWidth;
    });

    // Add some neon signs
    const neonColors = [0xff00ff, 0x00ffff, 0xffff00, 0xff6600];
    for (let i = 0; i < 6; i++) {
      const nx = Phaser.Math.Between(50, GAME_WIDTH - 50);
      const ny = Phaser.Math.Between(this.groundY - 200, this.groundY - 80);
      const color = Phaser.Utils.Array.GetRandom(neonColors);
      graphics.fillStyle(color, 0.8);
      graphics.fillRect(nx, ny, Phaser.Math.Between(15, 40), Phaser.Math.Between(8, 15));

      // Glow effect
      graphics.fillStyle(color, 0.2);
      graphics.fillRect(nx - 5, ny - 5, Phaser.Math.Between(25, 50), Phaser.Math.Between(18, 25));
    }
  }

  createGround() {
    const graphics = this.add.graphics();
    graphics.setDepth(5);

    // Ground surface
    graphics.fillStyle(0x2a2a2a, 1);
    graphics.fillRect(0, this.groundY, GAME_WIDTH, 100);

    // Ground line highlight
    graphics.fillStyle(0x444444, 1);
    graphics.fillRect(0, this.groundY, GAME_WIDTH, 3);

    // Add some ground details
    graphics.fillStyle(0x333333, 1);
    for (let i = 0; i < 20; i++) {
      const gx = Phaser.Math.Between(0, GAME_WIDTH);
      graphics.fillRect(gx, this.groundY + 10, Phaser.Math.Between(20, 60), 2);
    }
  }

  createStarfield() {
    this.stars = [];
    for (let i = 0; i < 50; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(0, this.groundY - 220);
      const size = Phaser.Math.Between(1, 2);
      const alpha = Phaser.Math.FloatBetween(0.3, 0.8);
      const star = this.add.circle(x, y, size, 0xffffff, alpha);
      star.setDepth(0);
      star.speed = size * 0.2;
      this.stars.push(star);
    }

    // Add some colored stars
    const colors = [0xff00ff, 0x00ffff, 0xffff00];
    for (let i = 0; i < 8; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(0, this.groundY - 250);
      const color = Phaser.Utils.Array.GetRandom(colors);
      const star = this.add.circle(x, y, 2, color, 0.7);
      star.setDepth(0);
      star.speed = 0.3;
      this.stars.push(star);

      // Twinkle effect
      this.tweens.add({
        targets: star,
        alpha: { from: 0.7, to: 0.2 },
        duration: Phaser.Math.Between(500, 1500),
        yoyo: true,
        repeat: -1,
      });
    }
  }

  createAnimations() {
    // Soldier run cycle (8 frames, 300x360 each)
    this.anims.create({
      key: 'soldier_run',
      frames: this.anims.generateFrameNumbers('credits_soldier_run', { start: 0, end: 7 }),
      frameRate: 12,
      repeat: -1,
    });

    // Soldier shoot (8 frames, 380x350 each)
    this.anims.create({
      key: 'soldier_shoot',
      frames: this.anims.generateFrameNumbers('credits_soldier_shoot', { start: 0, end: 7 }),
      frameRate: 16,
      repeat: 0,
    });

    // Soldier idle (first frame of run)
    this.anims.create({
      key: 'soldier_idle',
      frames: [{ key: 'credits_soldier_run', frame: 0 }],
      frameRate: 1,
    });

    // Zombie walk (6 frames, 230x310 each)
    this.anims.create({
      key: 'zombie_walk',
      frames: this.anims.generateFrameNumbers('credits_zombie_walk', { start: 0, end: 5 }),
      frameRate: 8,
      repeat: -1,
    });

    // Zombie explode (10 frames, 330x340 each)
    this.anims.create({
      key: 'zombie_explode',
      frames: this.anims.generateFrameNumbers('credits_zombie_explode', { start: 0, end: 9 }),
      frameRate: 14,
      repeat: 0,
    });

    // Muzzle flash (4 frames, 320x320 each)
    this.anims.create({
      key: 'muzzle_flash',
      frames: this.anims.generateFrameNumbers('credits_muzzle', { start: 0, end: 3 }),
      frameRate: 20,
      repeat: 0,
    });
  }

  createSoldier() {
    // Soldier starts off-screen left
    this.soldier = this.add.sprite(-100, this.groundY, 'credits_soldier_run');
    this.soldier.setScale(0.35);
    this.soldier.setDepth(20);
    this.soldier.setOrigin(0.5, 1); // Feet on ground

    // Play run animation
    this.soldier.play('soldier_run');

    // Tween soldier to "home" position (right of credits overlay)
    this.soldierHomeX = 320;
    this.tweens.add({
      targets: this.soldier,
      x: this.soldierHomeX,
      duration: 2500,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.soldierState = 'defending';
        this.soldier.play('soldier_idle');
        // Start spawning zombies
        this.setupZombieSpawner();
      }
    });
  }

  setupZombieSpawner() {
    // Spawn first zombie after short delay
    this.time.delayedCall(500, () => this.spawnZombieWave());

    // Spawn zombie waves at intervals
    this.zombieSpawnTimer = this.time.addEvent({
      delay: 3200,
      callback: this.spawnZombieWave,
      callbackScope: this,
      loop: true,
    });
  }

  spawnZombieWave() {
    // Randomly spawn 1-3 zombies at a time
    const waveSize = Phaser.Math.Between(1, 3);

    for (let i = 0; i < waveSize; i++) {
      // Stagger spawn timing slightly for multiple zombies
      this.time.delayedCall(i * 400, () => this.spawnZombie());
    }
  }

  spawnZombie() {
    // Create zombie off-screen right with slight vertical variation
    const yOffset = Phaser.Math.Between(-5, 5);
    const zombie = this.add.sprite(GAME_WIDTH + 80, this.groundY + yOffset, 'credits_zombie_walk');
    zombie.setScale(0.4);
    zombie.setDepth(15);
    zombie.setOrigin(0.5, 1);
    zombie.setFlipX(true); // Face left toward soldier
    zombie.play('zombie_walk');
    zombie.isAlive = true;

    this.zombies.push(zombie);

    // Zombie walks left toward soldier
    this.tweens.add({
      targets: zombie,
      x: -100,
      duration: 14000,
      ease: 'Linear',
      onComplete: () => {
        this.removeZombie(zombie);
      }
    });
  }

  soldierShoot(targetZombie) {
    if (this.shootCooldown > 0 || !targetZombie || !targetZombie.isAlive) return;

    // Switch to shoot animation
    this.soldier.play('soldier_shoot');
    this.soldierState = 'shooting';
    this.shootCooldown = 600; // ms between shots

    // Create muzzle flash at gun position
    const muzzleX = this.soldier.x + 55;
    const muzzleY = this.soldier.y - 50;
    const flash = this.add.sprite(muzzleX, muzzleY, 'credits_muzzle');
    flash.setScale(0.15);
    flash.setDepth(21);
    flash.play('muzzle_flash');
    flash.once('animationcomplete', () => flash.destroy());

    // Create bullet
    this.createBullet(muzzleX + 10, muzzleY, targetZombie);

    // Play gunshot sound
    this.sound.play('gunshot', { volume: 0.3 });

    // Return to idle/run after shoot animation
    this.soldier.once('animationcomplete', () => {
      this.soldierState = 'defending';
      if (this.zombies.filter(z => z.isAlive).length > 0) {
        this.soldier.play('soldier_idle');
      } else {
        this.soldier.play('soldier_idle');
      }
    });
  }

  createBullet(x, y, target) {
    const bullet = this.add.image(x, y, 'credits_bullet');
    bullet.setScale(0.08);
    bullet.setDepth(19);
    bullet.target = target;
    this.bullets.push(bullet);

    // Tween bullet toward target
    this.tweens.add({
      targets: bullet,
      x: target.x - 30,
      y: target.y - 50,
      duration: 180,
      ease: 'Linear',
      onComplete: () => {
        this.bulletHitZombie(bullet, target);
      }
    });
  }

  bulletHitZombie(bullet, zombie) {
    // Remove bullet
    const bulletIndex = this.bullets.indexOf(bullet);
    if (bulletIndex > -1) {
      this.bullets.splice(bulletIndex, 1);
    }
    bullet.destroy();

    // Kill the zombie
    this.killZombie(zombie);
  }

  killZombie(zombie) {
    if (!zombie || !zombie.isAlive) return;

    zombie.isAlive = false;

    // Stop walk tween
    this.tweens.killTweensOf(zombie);

    // Play explosion animation
    zombie.play('zombie_explode');

    // Play impact sound
    this.sound.play('impact', { volume: 0.4 });

    // Screen shake (subtle)
    this.cameras.main.shake(80, 0.003);

    // Destroy after animation completes
    zombie.once('animationcomplete', () => {
      this.removeZombie(zombie);
    });
  }

  removeZombie(zombie) {
    const index = this.zombies.indexOf(zombie);
    if (index > -1) {
      this.zombies.splice(index, 1);
    }
    if (zombie && zombie.active) {
      zombie.destroy();
    }
  }

  createCreditsOverlay() {
    // Semi-transparent dark overlay for readability (left third of screen)
    const overlay = this.add.rectangle(
      130, GAME_HEIGHT / 2,
      260, GAME_HEIGHT,
      0x000000, 0.5
    );
    overlay.setDepth(50);

    // Credits container (scrolling on left side)
    this.creditsContainer = this.add.container(130, GAME_HEIGHT + 50);
    this.creditsContainer.setDepth(60);

    // Build credits
    const credits = this.buildCredits();
    let yOffset = 0;

    credits.forEach(item => {
      const textObj = this.add.text(0, yOffset, item.text, {
        fontFamily: 'Courier New',
        fontSize: item.size || '14px',
        fill: item.color || '#ffffff',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 2,
      });
      textObj.setOrigin(0.5, 0);
      this.creditsContainer.add(textObj);
      yOffset += item.spacing || 28;
    });

    this.totalCreditsHeight = yOffset;

    // Fixed header
    const headerBg = this.add.rectangle(GAME_WIDTH / 2, 30, GAME_WIDTH, 60, 0x000000, 0.7);
    headerBg.setDepth(90);

    const headerText = this.add.text(GAME_WIDTH / 2, 30, 'MISSION COMPLETE', {
      fontFamily: 'Courier New',
      fontSize: '28px',
      fill: '#00ff00',
      stroke: '#000000',
      strokeThickness: 4,
    });
    headerText.setOrigin(0.5);
    headerText.setDepth(100);

    // Pulse header
    this.tweens.add({
      targets: headerText,
      alpha: { from: 1, to: 0.6 },
      duration: 800,
      yoyo: true,
      repeat: -1,
    });
  }

  buildCredits() {
    return [
      { text: '- - - - - - - - -', color: '#ff00ff', size: '12px', spacing: 30 },
      { text: '', spacing: 20 },
      { text: '1V1 STUDIOS', color: '#00ffff', size: '22px', spacing: 35 },
      { text: 'PRESENTS', color: '#ff00ff', size: '14px', spacing: 50 },
      { text: '* * * * * * * *', color: '#ffff00', size: '10px', spacing: 35 },
      { text: '', spacing: 20 },
      { text: 'LEAD DEVELOPER', color: '#00ff00', size: '10px', spacing: 18 },
      { text: 'Hayden "Sniper" B', color: '#ffffff', size: '16px', spacing: 40 },
      { text: 'GENERAL MANAGER', color: '#00ff00', size: '10px', spacing: 18 },
      { text: 'Graham "Rad Bad Dad" B', color: '#ffffff', size: '16px', spacing: 40 },
      { text: 'LEVEL EDITOR', color: '#00ff00', size: '10px', spacing: 18 },
      { text: 'Hayden "Sniper" B', color: '#ffffff', size: '16px', spacing: 40 },
      { text: 'STORYLINE', color: '#00ff00', size: '10px', spacing: 18 },
      { text: 'Graham "Rad Bad Dad" B', color: '#ffffff', size: '16px', spacing: 40 },
      { text: '- - - - - - - - -', color: '#ff00ff', size: '12px', spacing: 35 },
      { text: '', spacing: 15 },
      { text: 'AUDIO EFFECTS', color: '#00ff00', size: '10px', spacing: 18 },
      { text: 'The Motion Monkey', color: '#ffffff', size: '16px', spacing: 22 },
      { text: 'themotionmonkey.co.uk', color: '#888888', size: '10px', spacing: 50 },
      { text: '* * * * * * * *', color: '#ffff00', size: '10px', spacing: 35 },
      { text: '', spacing: 20 },
      { text: 'COPYRIGHT 2026', color: '#ff6600', size: '12px', spacing: 22 },
      { text: '1v1studios', color: '#ff6600', size: '16px', spacing: 22 },
      { text: '1v1studios.com', color: '#888888', size: '10px', spacing: 50 },
      { text: '', spacing: 30 },
      { text: '= = = = = = = = =', color: '#ff00ff', size: '12px', spacing: 30 },
      { text: '', spacing: 40 },
      { text: 'DEDICATED TO', color: '#ff6699', size: '12px', spacing: 22 },
      { text: '', spacing: 15 },
      { text: 'JESSIE', color: '#ff6699', size: '28px', spacing: 40 },
      { text: '', spacing: 40 },
      { text: '= = = = = = = = =', color: '#ff00ff', size: '12px', spacing: 50 },
      { text: '', spacing: 60 },
      { text: 'THANKS FOR', color: '#00ffff', size: '16px', spacing: 22 },
      { text: 'PLAYING!', color: '#00ffff', size: '20px', spacing: 40 },
      { text: '', spacing: 80 },
    ];
  }

  createScanlines() {
    const graphics = this.add.graphics();
    graphics.setDepth(200);
    graphics.setScrollFactor(0);

    for (let y = 0; y < GAME_HEIGHT; y += 3) {
      graphics.fillStyle(0x000000, 0.1);
      graphics.fillRect(0, y, GAME_WIDTH, 1);
    }
  }

  setupInput() {
    // Skip handlers
    this.input.keyboard.on('keydown-ENTER', () => this.exitScene());
    this.input.keyboard.on('keydown-SPACE', () => this.exitScene());
    this.input.keyboard.on('keydown-ESC', () => this.exitScene());
    this.input.on('pointerdown', () => this.exitScene());

    // Skip text hint
    const skipText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 18, 'PRESS ENTER TO CONTINUE', {
      fontFamily: 'Courier New',
      fontSize: '11px',
      fill: '#ffff00',
      stroke: '#000000',
      strokeThickness: 2,
    });
    skipText.setOrigin(0.5);
    skipText.setDepth(100);

    this.tweens.add({
      targets: skipText,
      alpha: { from: 1, to: 0.3 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });
  }

  update(time, delta) {
    // Update shoot cooldown
    if (this.shootCooldown > 0) {
      this.shootCooldown -= delta;
    }

    // Auto-shoot at nearest zombie when defending
    if (this.soldierState === 'defending' && this.zombies.length > 0) {
      let closestZombie = null;
      let closestDist = Infinity;

      this.zombies.forEach(zombie => {
        if (zombie.isAlive && zombie.x > this.soldier.x && zombie.x < GAME_WIDTH) {
          const dist = zombie.x - this.soldier.x;
          if (dist < closestDist) {
            closestDist = dist;
            closestZombie = zombie;
          }
        }
      });

      // Only shoot when zombie is closer (let them come in more from screen edge)
      if (closestZombie && closestDist < 380) {
        this.soldierShoot(closestZombie);
      }
    }

    // Scroll credits
    if (this.creditsContainer) {
      this.creditsContainer.y -= 0.5;

      // Loop credits when they scroll off
      if (this.creditsContainer.y < -this.totalCreditsHeight - 50) {
        this.creditsContainer.y = GAME_HEIGHT + 50;
      }
    }

    // Animate starfield
    this.stars.forEach(star => {
      star.y -= star.speed * 0.3;
      if (star.y < -5) {
        star.y = this.groundY - 220;
        star.x = Phaser.Math.Between(0, GAME_WIDTH);
      }
    });
  }

  exitScene() {
    // Clean up timers
    if (this.zombieSpawnTimer) {
      this.zombieSpawnTimer.destroy();
    }

    this.input.keyboard.off('keydown-ENTER');
    this.input.keyboard.off('keydown-SPACE');
    this.input.keyboard.off('keydown-ESC');
    this.input.off('pointerdown');

    // Fade out music
    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 800,
      });
    }

    // Fade out and return to menu
    this.cameras.main.fadeOut(800, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      if (this.music) this.music.stop();
      this.scene.start('MenuScene');
    });
  }
}
